<template>
	<div class="card mb-6">

		<div class="grid ml-0">
			<div class="col-12 grid">
				<div class="flex flex-auto p-input-icon-left p-input-icon-right mt-1">
					<i class="pi pi-home" />
					<InputText v-model="filterText" placeholder="Property keyword filter.." @input="filterChanged" />
					<i class="pi pi-search" />
				</div>

				<a @click="dashboardExcelExport" class="css-image excel-icon mt-1" v-tooltip.top="'Export to Excel'"></a>
			</div>
			<div class="col-12 grid text-right pt-1">
				<div class="flex mt-1 mr-4">
					<Dropdown v-model="selectedService" :options="servicesList" optionLabel="serviceName" placeholder="Select service" :filter="true" filterPlaceholder="Find a service" class="w-100pc" @change="search" />
				</div>
				<div class="flex mt-1 mr-4">
					<Calendar v-model="startDate" class="w-100pc" :showIcon="true" dateFormat="d M yy" placeholder="Start date" :showButtonBar="true" />
				</div>
				<div class="flex mt-1 mr-4">
					<Calendar v-model="endDate" class="w-100pc" :showIcon="true" dateFormat="d M yy" placeholder="End date" :showButtonBar="true" />
				</div>
				<div class="flex mt-1 align-items-center">
					<InputSwitch v-model="activeProperty" class="mr-1" @change="search(true)" />
					<a class="mr-4" @click="activeProperty=!activeProperty">Active properties only</a>
				</div>
				<div class="flex mt-1 align-items-center">
					<InputSwitch v-model="activeService" class="mr-1" @change="search(true)" />
					<a class="mr-4" @click="activeService=!activeService">Active services only</a>
				</div>
				<div class="flex mt-1 align-items-center" v-if="isInternalUser && selectedService.serviceId == BwofServiceId">
					<InputSwitch v-model="readyToIssue" class="mr-1" @change="search(true)" />
					<a class="mr-4" @click="readyToIssue=!readyToIssue">Ready to issue</a>
				</div>
				<div class="flex mt-1 align-items-center" v-if="isInternalUser">
					<InputSwitch v-model="includeBwofAnniversary" class="mr-1" @change="search(true)" />
					<a class="mr-4" @click="includeBwofAnniversary=!includeBwofAnniversary">Include Bwof Anniversary</a>
				</div>
			</div>
		</div>

		<Message v-if="searchError" severity="error">{{searchError}}</Message>

		<div>
			<DataView :value="dashboardListData"
					  datakey="propertyId"
					  :paginator="true"
					  :rows="100"
					  :scrollable="true"
					  stripedRows
					  class="p-datatable-sm"
					  :paginatorTemplate="paginatorTemplate"
					  :rowsPerPageOptions="[10,20,50,100,200]"
					  responsiveLayout="scroll"
					  :alwaysShowPaginator="false"
					  currentPageReportTemplate="Showing {first} to {last} of {totalRecords}">

				<template #header>
					<div class="col-12">
						<div class="grid flex-nowrap">
							<div class="col-fixed">ID</div>
							<div class="col">Property</div>
							<div class="col">Service</div>
							<div class="col hidden lg:block">Supplier</div>
							<div class="col-fixed hidden xl:block daysColumn pl-3">Due days</div>
							<div class="col-fixed hidden lg:block dateColumn">Due date</div>
							<div class="col-fixed hidden xl:block dateColumn">Previous date</div>
							<div v-if="includeBwofAnniversary" class="col-fixed hidden lg:block dateColumn pl-4">Bwof anniversary</div>
							<div v-if="displayCompletedHeader" class="col-fixed hidden lg:block calendarColumnHeader">New previous date</div>
							<div v-if="displayCompletedHeader" class="col-fixed hidden lg:block completedColumn">Completed?</div>
							<div class="col-fixed hidden md:block infoColumn"></div>
						</div>
					</div>
				</template>

				<template #list="{data}">
					<div class="col-12 property-dashboard-item hover-row" v-bind:class="rowStatus(data)">
						<div class="grid flex-nowrap" v-tooltip.bottom="getNotes(data)">
							<div class="col-fixed ml-1">
								<router-link :to="{ name: 'PropertyDetails', params: { propertyId: data.propertyId }}">{{data.propertyId}}</router-link>
							</div>
							<div class="col">
								<router-link :to="{ name: 'PropertyDetails', params: { propertyId: data.propertyId }}">{{data.propertyNameAndLocation }}<br /><small>{{data.customerName}}</small></router-link>
								<Badge v-if="!data.propertyActive" class="small-grey-badge ml-1" value="inactive" />
							</div>
							<div class="col">
								<router-link :to="{ name: 'PropertyDetails', params: { propertyId: data.propertyId }}">
									{{data.serviceName}}
									<br />
									<small v-if="showBwofStatistics">{{data.readyBwofSubServices}} of {{data.totalBwofSubServices}}</small>
								</router-link>
								<Badge v-if="!data.serviceActive" class="small-grey-badge ml-1" value="inactive" />
							</div>
							<div class="col hidden lg:block">
								{{data.supplierName}}
							</div>
							<div class="col-fixed hidden xl:block daysColumn">
								<span>{{data.days}}</span>
							</div>
							<div class="col-fixed hidden lg:block dateColumn">
								<span>{{formattedDate(data.due)}}</span>
							</div>
							<div class="col-fixed hidden xl:block dateColumn">
								<span>{{formattedDate(data.lastDue)}}</span>
							</div>
							<div v-if="includeBwofAnniversary" class="col-fixed hidden lg:block dateColumn">
								<span>{{formattedDate(data.bwofDue)}}</span>
							</div>
							<div v-if="displayUpdateAnniversaryCheckbox(data)" class="col-fixed hidden lg:block calendarColumn my-1">
								<Calendar v-model="data.newPreviousDate" :showIcon="true" dateFormat="d M yy" />
							</div>
							<div v-if="displayUpdateAnniversaryCheckbox(data)" class="col-fixed hidden lg:block my-1 completedColumn">
								<Button icon="pi pi-check" aria-label="Mark as Complete" @click="handleComplete(data)" />
							</div>
							<div class="col-fixed hidden md:block infoColumn">
								<a @click="clickSidebar(data)"><i class="pi pi-info-circle"></i></a>
							</div>
						</div>
					</div>
				</template>
			</DataView>

			<!-- Details side bar-->
			<Sidebar v-model:visible="showSidebar" :baseZIndex="1000" position="right" class="p-sidebar-md">
				<h3><a @click="click(sidebarData)">{{sidebarData.propertyName}}</a></h3>
				<div class="grid">
					<div class="col-2 label">
						Address
					</div>
					<div class="col-10">
						{{sidebarData.streetAddress}} {{sidebarData.location}}
					</div>
					<div class="col-2 label" v-if="isInternalUser">
						Customer
					</div>
					<div class="col-10" v-if="isInternalUser">
						<!-- Contractors can't click to get to company record -->
						<a @click="customerClick(sidebarData.custId)">
							{{sidebarData.customerName}}
						</a>
					</div>
					<div class="col-12 label">
						Exception Notes
					</div>
					<div class="col-12 pre-wrap">
						<small>{{sidebarData.exceptionNotes}}</small>
					</div>
				</div>
			</Sidebar>
		</div>
	</div>

	<ProgressSpinner v-if="loading" />
</template>

<script lang="ts">
	import { Vue, Options } from 'vue-class-component';
	import { PropertyServiceData, PropertySummary, GetPropertyDashboardQuery, PropertyDashboardData, GetAllAccessibleServicesQuery, ServiceData, UpdatePropertyServiceDatesCommand } from '@/api-client/api-client'
	import { CompassApi } from '@/api-client/CompassApiProxy'
	import { format, isValid, add } from 'date-fns';
	import filterListBySearchString from '../utils/StringSearch';
	import { DownloadFromFileResponse } from '../utils/DownloadFromFileResponse';
	import { GetBwofServiceID, GetOwnerInspectionServiceId } from '../utils/CompassConstants';
	import Toast from '@/utils/Toast';

	@Options({
		name: 'PropertyList',
		emits: ['updateOverdue', 'updateUpcoming', 'updateAway', 'updateCompleted', 'updateSelectedService'],
		components: {
		},
		props: {
		},
		watch: {
			startDate(_value: Date) {
				this.search(true);
			},
			endDate(_value: Date) {
				this.search(true);
			},
			dashboardListData(value: PropertyList) {
				// When dashboard list changes, update the doughnut
				this.updateDoughnut(value);
			}
		}
	})
	export default class PropertyList extends Vue {

		// Data
		filterText = "";
		debouncedFilterText = "";
		debounceTimerRef = -1;
		showSidebar = false;
		sidebarData: PropertyServiceData | null = null;
		services: ServiceData[] = [];
		activeProperty = true;
		activeService = true;
		startDate: Date = new Date(2000, 0, 1);
		endDate: Date = add(new Date(), { months: 1 });
		loading = true;
		dashboardList: PropertyDashboardData[] = [];
		selectedService: ServiceData = new ServiceData();
		searchError?: string = "";
		isInternalUser = false;
		isCustomer = false;
		readyToIssue = false;
		includeBwofAnniversary = false;
		BwofServiceId = GetBwofServiceID();
		ownerInspectionServiceId = GetOwnerInspectionServiceId();

		created() {
			// Set intenalUser flag based on token
			this.isInternalUser = this.$store.getters.isInternalUser;
			this.isCustomer = this.$store.getters.isCustomer;
		}

		/** Delay a few milliseconds after typing a character so we don't tie up the UI thread */
		filterChanged(_ev: InputEvent) {
			const timeoutMillseconds = 100;
			clearTimeout(this.debounceTimerRef);
			this.debounceTimerRef = setTimeout(() => this.debouncedFilterText = this.filterText, timeoutMillseconds);
		}

		/**
		 * Perform the search
		 * @param showLoading is whether we want the loading circle to show. Pass as false to allow any existing (possibly stale) data to still show while it loads the new data
		 */
		search(showLoading: boolean) {
			if (showLoading || this.dashboardList.length == 0) {
				// Show the loading flag and clear any stale data
				this.loading = true;
				this.dashboardList = [];
			}

			// set selected service (Doughnut text)
			this.$emit('updateSelectedService', this.selectedService);

			// Prepare payload to send to server
			const dashboardData = new GetPropertyDashboardQuery();
			dashboardData.queryString = undefined;  // Attempt filtering this client-side rather than on server

			dashboardData.startDate = this.startDate;
			dashboardData.endDate = this.endDate;
			dashboardData.activeProperty = this.activeProperty;
			dashboardData.activeService = this.activeService;
			dashboardData.readyToIssue = this.selectedService.serviceId == this.BwofServiceId ? this.readyToIssue : false;
			dashboardData.serviceId = this.selectedService.serviceId;
			dashboardData.includeSubServices = this.includeSubServices;
			dashboardData.includeBwofAnniversary = this.includeBwofAnniversary;

			const apiClient = CompassApi.propertyApi(this.$store);
			apiClient.getPropertyDashboard(dashboardData)
				.then(result => {
					this.dashboardList = result;
					this.loading = false;
					this.updateDoughnut(this.dashboardListData);
				})
				.catch(error => {
					this.loading = false;
					this.searchError = error;
					Toast.ApiError('Error retrieving dashboard records', error);
				});
		}

		/** The parent component calls this on every entry to the page */
		loadServicesAndSearch() {
			// Ensure the services are loaded before performing the first search (it may only have our 'leave blank to choose all' placeholder)
			if (this.services.length <= 1) {
				// If they're not loaded, load them, and in the return, perform the search
				const apiClient = CompassApi.serviceApi(this.$store);

				const servicesQuery = new GetAllAccessibleServicesQuery();
				servicesQuery.sortType = 'main-first';
				servicesQuery.onlyServicesInUse = true;
				
				apiClient.getAllServices(servicesQuery)
					.then(result => {
						this.services = result;
						this.selectedService = this.services.filter(s => s.serviceName == 'BWOF')[0];

						this.search(true); // Search only after default service has been loaded
					})
					.catch(error => {
						Toast.ApiError('Error retrieving services list', error);
					});
			}
			else {
				// If they're already loaded, we can just perform the search (and don't show loading, so it allows stale data in the meantime)
				this.search(false);
			}
		}

		click(data: PropertySummary) {
			this.$router.push({
				name: 'PropertyDetails',
				params: { propertyId: data.propertyId }
			});

			// If clicks on sidebar title, needs to be hidden
			this.showSidebar = false;
		}

		customerClick(id: number) {
			this.$router.push({
				name: 'CompanyDetails',
				params: { companyId: id }
			});
			this.showSidebar = false;
		}

		clickSidebar(data: PropertyServiceData) {
			this.showSidebar = true;
			this.sidebarData = data;
		}

		updateDoughnut(listData: PropertyDashboardData[]) {
			const overdue = listData.filter(item => item.days == null || item.days <= 6).length;
			const upcoming = listData.filter(item => item.days ? item.days > 6 && item.days < 21 : false).length;
			const away = listData.filter(item => item.days ? item.days >= 21 && item.days < 30 : false).length;
			const completed = listData.filter(item => item.days ? item.days >= 30 : false).length;

			this.$emit('updateOverdue', overdue);
			this.$emit('updateUpcoming', upcoming);
			this.$emit('updateAway', away);
			this.$emit('updateCompleted', completed);
		}

		// Helpers
		formattedDate(value: Date) {
			if (!isValid(value)) return;

			return format(value, 'dd MMM yyyy');
		}

		rowStatus(data: PropertyDashboardData) {
			const dif = data.days;
			if (dif) {
				if (dif <= 6) {
					return 'overdue';
				}
				if (dif > 6 && dif <= 21) {
					return 'pending';
				}
				if (dif > 21 && dif <= 30) {
					return 'away';
				}

				// completed
				return;
			}

			// null (dueDays == 0)
			return 'overdue';
		}

		getNotes(data: PropertyDashboardData) {
			if (data.exceptionNotes != null && data.exceptionNotes.length > 0)
				return data.exceptionNotes;
			else
				return '(No exception notes)';
		}

		dashboardExcelExport() {

			const dashboardData = new GetPropertyDashboardQuery();
			dashboardData.queryString = this.filterText;
			dashboardData.startDate = this.startDate;
			dashboardData.endDate = this.endDate;
			dashboardData.activeProperty = this.activeProperty;
			dashboardData.activeService = this.activeService;
			dashboardData.serviceId = this.selectedService.serviceId;
			dashboardData.readyToIssue = this.selectedService.serviceId == this.BwofServiceId ? this.readyToIssue : false;
			dashboardData.includeSubServices = this.includeSubServices;
			dashboardData.includeBwofAnniversary = this.includeBwofAnniversary;

			const apiClient = CompassApi.propertyApi(this.$store);
			apiClient.dashboardExcelExport(dashboardData)
				.then(result => {

					// Download Popup
					if (result)
						DownloadFromFileResponse(result);

					Toast.RemoveAllGroups();
					Toast.Success('Dashboard Excel export', "The export was successful.");
				})
				.catch(error => {
					Toast.ApiError("Error exporting to excel", error);
				});
		}

		handleComplete(rowData: PropertyDashboardData) {

			// Nothing to update
			if(!rowData.newPreviousDate){
				Toast.Info("New due date", "Please set a new due date before marking inspection as complete");
				return;
			}

			this.loading = true;
			Toast.RemoveAllGroups();

			const updatePropertyServiceDatesCommand = new UpdatePropertyServiceDatesCommand();
			updatePropertyServiceDatesCommand.propertyServiceId = rowData.propertyServiceId;
			updatePropertyServiceDatesCommand.newPreviousDate = rowData.newPreviousDate;

			const apiClient = CompassApi.propertyApi(this.$store);
			apiClient.updatePropertyServiceDates(updatePropertyServiceDatesCommand)
				.then(result => {

					Toast.Success("Successfully updated", "Service dates were successfully updated", 1000)
					this.search(true);
					this.loading = false;
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError('Error retrieving updating property service', error);
				});
		}

		// Currently only enabled for overdue Owner Inspection Service & internal users
		// Future: could consider only displaying for certain statuses (e.g overdue)
		displayUpdateAnniversaryCheckbox(rowData: PropertyDashboardData) {
			return this.selectedService.serviceId === this.ownerInspectionServiceId && rowData.serviceId === this.ownerInspectionServiceId && this.isInternalUser;
		}

		// Currently only enabled for Owner Inspection Service for internal users
		// Future: When enabled for all services could toggle editing with padlock
		get displayCompletedHeader() {
			return this.selectedService.serviceId === this.ownerInspectionServiceId && this.isInternalUser;
		}

		get dashboardListData() {
			let list = this.dashboardList;

			if (this.activeProperty)
				list = list.filter(item => item.propertyActive === true);

			if (this.activeService)
				list = list.filter(item => item.serviceActive === true);

			list = filterListBySearchString(list, this.debouncedFilterText, (item: PropertyDashboardData) =>
				item.propertyId
				+ ' ' + item.propertyNameAndLocation
				+ ' ' + item.serviceName
				+ ' ' + item.customerPropertyRef
				+ ' ' + item.customerName
				+ ' ' + item.supplierName);

			return list;
		}

		get servicesList() {
			const blankServiceEntry: ServiceData = new ServiceData(
				{
					serviceId: 0,
					serviceName: "All Services",
					isCategory: true,
					active: true
				}
			);
			this.services.unshift(blankServiceEntry);
			return this.services;
		}

		get includeSubServices() {
			return this.selectedService?.serviceId == this.BwofServiceId;
		}

		get showBwofStatistics() {
			return (this.isInternalUser || this.isCustomer) && this.includeSubServices;
		}

		// Computed isDesktop
		get isDesktop() {
			return window.innerWidth >= 992;
		}

		// Computed to simplify paginator on mobile
		get paginatorTemplate() {
			return this.isDesktop ?
				"CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
				: "PrevPageLink NextPageLink"
		}
	}
</script>

<style scoped lang="scss">
	.daysColumn {
		width: 3.25rem;
	}

	// Generic date column
	.dateColumn {
		width: 7.25rem;
	}

	.calendarColumnHeader {
		width: 9.5rem;
		margin-left: 1rem;
	}

	.calendarColumn {
		max-width: 12.5rem;
		flex: 1;
	}

	// Column with completed button
	.completedColumn {
		width: 3rem;
	}

	// Far right column
	.infoColumn {
		width: 2rem;
	}

	.overdue {
		background-color: #FF8F8F; // reddish pink from original system
		color: #000;

		&:hover, &:hover a {
			color: #fff;
			background-color: indianred !important;
		}
	}

	.pending {
		background-color: #FFD375; // orange from original system
		color: #000;

		&:hover, &:hover a {
			color: #000 !important;
			background-color: #FF9800 !important;
		}
	}

	.away {
		background-color: #9CF1A0; // green from original system
		color: #000;

		&:hover, &:hover a {
			color: #fff;
			background-color: limegreen !important;
		}
	}

	@media (max-width: 1200px) {
		.calendarColumnHeader {
			width: 5.5rem;
			margin-left: 0rem;
		}
	}
</style>

<!-- Unscoped Styles (can apply to other included components) -->
<style>
	/* Normal tooltip is limited to 200px, and has a larger font */
	div.p-tooltip {
		max-width: 90%;
		font-size: 13px;
	}
</style>